// Окно общего доступа: кто правит поездку вместе с владельцем.
//
// Приглашают по адресу почты — это единственный идентификатор человека, который
// приглашающий и так знает. Аккаунт не обязателен: на незнакомую почту уходит
// письмо-приглашение, а доступ выдаётся сам после регистрации этого адреса
// (сервер отвечает pending, окно объясняет это заметкой).
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { Modal } from '../ui/shell';
import { useI18n, useT } from '../i18n';
import { useAuth } from '../auth';
import * as api from './api';
import type { TripAccess, TripMember, TripMeta } from './api';
import styles from './TripAccessModal.module.css';

/** Коды отказов сервера, для которых у нас есть свой текст. */
const ERRORS = new Set(['no_user', 'already_member', 'is_owner', 'too_many', 'invalid_email', 'blocked']);

function errorText(code: string | undefined): string {
  return code && ERRORS.has(code) ? `trips.access.err.${code}` : 'trips.access.err.unknown';
}

export interface TripAccessModalProps {
  trip: TripMeta;
  onClose(): void;
  /** Состав участников изменился — список поездок пора перечитать. */
  onChanged(): void;
}

function MemberRow({
  member,
  you,
  canRemove,
  removeLabel,
  onRemove,
}: {
  member: TripMember;
  you: boolean;
  canRemove: boolean;
  removeLabel: string;
  onRemove(): void;
}): ReactElement {
  const t = useT();
  const name = member.name.trim();
  return (
    <li className={styles.member}>
      <div className={styles.who}>
        <span className={styles.name}>
          {name || member.email}
          {you && <span className={styles.you}> · {t('trips.access.you')}</span>}
        </span>
        {/* Имени может не быть — тогда его место уже занял адрес, и второй
            строкой он бы просто повторился. */}
        {name && <span className={styles.email}>{member.email}</span>}
      </div>
      <span className={styles.role}>
        {t(member.role === 'owner' ? 'trips.access.role.owner' : 'trips.access.role.editor')}
      </span>
      {canRemove ? (
        <button type="button" className="btn btn-sm btn-danger" onClick={onRemove}>
          {removeLabel}
        </button>
      ) : (
        <span className={styles.spacer} aria-hidden="true" />
      )}
    </li>
  );
}

export function TripAccessModal({ trip, onClose, onChanged }: TripAccessModalProps): ReactElement {
  const t = useT();
  const { lang } = useI18n();
  const { user } = useAuth();

  const [access, setAccess] = useState<TripAccess | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Почта без аккаунта: приглашение отложено, показываем это не как ошибку.
  const [pendingFor, setPendingFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.listMembers(trip.id);
    setAccess(res.ok ? (res.value ?? null) : null);
  }, [trip.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const isOwner = (access?.role ?? trip.role) === 'owner';

  async function handleInvite(e: FormEvent): Promise<void> {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setBusy(true);
    setError(null);
    setPendingFor(null);
    const res = await api.addMember(trip.id, value, lang);
    setBusy(false);
    if (!res.ok) {
      setError(errorText(res.error.error));
      return;
    }
    setEmail('');
    if (res.value.pending) setPendingFor(res.value.email ?? value);
    await load();
    onChanged();
  }

  async function handleRemove(userId: string): Promise<void> {
    setBusy(true);
    const res = await api.removeMember(trip.id, userId);
    setBusy(false);
    if (!res.ok) {
      setError(errorText(res.error.error));
      return;
    }
    // Ушёл сам — поездки больше нет в списке, и открытое окно ни к чему.
    if (userId === user?.id) {
      onChanged();
      onClose();
      return;
    }
    await load();
    onChanged();
  }

  return (
    <Modal title={t('trips.access.title', { name: trip.name.trim() || t('trips.untitled') })} onClose={onClose}>
      <div className={styles.body}>
        <p className={styles.lead}>{t('trips.access.lead')}</p>

        {access === null ? (
          <p className={styles.muted}>{t('auth.busy')}</p>
        ) : (
          <ul className={styles.list}>
            <MemberRow
              member={access.owner}
              you={access.owner.userId === user?.id}
              canRemove={false}
              removeLabel=""
              onRemove={() => {}}
            />
            {access.members.map((member) => (
              <MemberRow
                key={member.userId}
                member={member}
                you={member.userId === user?.id}
                // Владелец снимает доступ любому; соавтор — только себе, и для
                // него это не «убрать», а «выйти».
                canRemove={isOwner || member.userId === user?.id}
                removeLabel={t(member.userId === user?.id ? 'trips.leave' : 'trips.access.remove')}
                onRemove={() => void handleRemove(member.userId)}
              />
            ))}
            {access.members.length === 0 && (
              <li className={styles.empty}>{t('trips.access.empty')}</li>
            )}
          </ul>
        )}

        {error && (
          <p className={styles.error} role="alert">
            {t(error)}
          </p>
        )}

        {pendingFor && (
          <p className={styles.pending} role="status">
            {t('trips.access.pending', { email: pendingFor })}
          </p>
        )}

        {isOwner && (
          <form className={styles.invite} onSubmit={handleInvite}>
            <label className={styles.label} htmlFor="trip-invite-email">
              {t('trips.access.invite')}
            </label>
            <div className={styles.inviteRow}>
              <input
                id="trip-invite-email"
                className="input"
                type="email"
                autoComplete="off"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
              />
              <button type="submit" className="btn btn-primary" disabled={busy || !email.trim()}>
                {t('trips.access.add')}
              </button>
            </div>
            <p className={styles.hint}>{t('trips.access.hint')}</p>
          </form>
        )}
      </div>
    </Modal>
  );
}
